/**
 * UsageService
 *
 * Owns all UI for the Basic-tier daily message quota:
 *
 *   - #usage-banner — global bottom-of-page slide-up (last child of `.app`,
 *     below `.app__main`), visible on every page in warning AND exhausted
 *     states. Carries the message + upgrade link; no close button —
 *     visibility is purely state-driven.
 *   - Lock overlay — exhausted state only, deferred until message:complete
 *     so the user's last exchange plays out first. Chat: title, subtitle,
 *     and amber fill, with the "Upgrade" CTA riding the floating PillRow
 *     above the input. Dashboard: same title/subtitle/amber-fill treatment,
 *     but with an inline "Upgrade" text-link appended to the subtitle
 *     instead, since the PillRow never renders on dashboard.
 *
 * Both pages use the same `input:disabled`/`input:enabled` bus contract (the
 * same path QuestionnaireLauncher uses).
 *
 * State is derived from `{ unlimited, used, limit }`:
 *   - unlimited  — Premium/Guest/Discovery/Intake/orientation_advice
 *   - normal     — used < 0.8 × limit
 *   - warning    — 0.8 × limit ≤ used < limit
 *   - exhausted  — used ≥ limit
 *
 * Responses arrive via two channels:
 *   1. `applyServerUsage(usage)` — called from ModeOrchestrator on every chat
 *      response (success + 429). Primary data flow.
 *   2. `initialize()` — one-shot GET /subscription/usage on post-auth boot for
 *      a non-guest. Guests are quota-exempt and the route is `requireAuth`,
 *      so a guest boot skips the fetch and re-arms on `user:updated` (emitted
 *      by `handleGuestConversion`).
 */

import { SubscriptionAPI } from '../core/subscriptionApi.js';
import { goToUpgrade } from '../utils/upgradeNavigation.js';

const WARN_RATIO = 0.8;

// Cap on the exhausted-state reset timer. `resetAt` is the next UTC midnight
// (at most ~24h out); an early fire past the cap just re-checks and no-ops.
const MAX_RESET_TIMER_MS = 24 * 60 * 60 * 1000;

export class UsageService {
  /**
   * @param {Object} eventBus - App-wide event bus
   * @param {Object} currentUser - User object from auth
   */
  constructor(eventBus, currentUser) {
    this.eventBus = eventBus;
    this.currentUser = currentUser;

    // Latest usage snapshot from the server (or mock state from devtools).
    this._usage = {
      unlimited: true,
      limit: null,
      used: null,
      remaining: null,
      resetAt: null
    };

    // Tracks whether the lock overlay is currently showing. When a send
    // lands on the exhausted state mid-stream we defer to message:complete;
    // this flag prevents duplicate emits and tells us when to emit input:enabled.
    this._overlayShown = false;

    // Last rendered banner content. #usage-banner is a role="status" live
    // region — rewriting identical innerHTML would re-announce to screen
    // readers on every chat response and destroy keyboard focus on the
    // upgrade button, so _render() short-circuits when nothing changed.
    this._lastBanner = { show: false, text: '' };
    this._bannerClearTimer = null;

    // Single timer armed while exhausted, firing at resetAt so a tab left
    // locked past UTC midnight unlocks itself (the disabled input never
    // reaches isBlocked(), so nothing else would notice the rollover).
    this._resetTimer = null;

    // Analytics transition dedup, keyed per event by the snapshot's `resetAt`
    // (the UTC-day identity). In-memory only, so a reload may re-fire each
    // event once — accepted. A new UTC day carries a new resetAt and
    // legitimately fires again.
    this._analyticsFiredFor = {};

    this._onUserUpdated = this._handleUserUpdated.bind(this);
    this._onMessageComplete = this._handleMessageComplete.bind(this);
    this._onPageChanged = this._handlePageChanged.bind(this);
    this._subscribed = false;
  }

  /**
   * Subscribe to events and fetch initial usage. Called once post-auth.
   */
  async initialize() {
    if (!this._subscribed) {
      this.eventBus.on?.('user:updated', this._onUserUpdated);
      this.eventBus.on?.('message:complete', this._onMessageComplete);
      this.eventBus.on?.('page:changed', this._onPageChanged);
      this._subscribed = true;
    }

    // Guests are quota-exempt and GET /subscription/usage is requireAuth; the
    // constructor default (`unlimited: true`) already renders no banner. The
    // bus subscriptions above stay armed so conversion can re-run this.
    if (this.currentUser?.isGuest) return;

    try {
      const result = await SubscriptionAPI.getUsage();
      if (result?.success && result.data) {
        this.applyServerUsage(result.data);
      } else {
        this._render();
      }
    } catch (err) {
      console.error('[UsageService] initialize failed:', err);
      this._render();
    }
  }

  /**
   * Apply a usage snapshot from a server response. Called by ModeOrchestrator
   * (success metadata and 429 body) and by `initialize()`.
   *
   * @param {Object} usage
   */
  applyServerUsage(usage) {
    if (!usage || typeof usage !== 'object') return;

    this._usage = {
      unlimited: Boolean(usage.unlimited),
      limit: usage.limit ?? null,
      used: usage.used ?? null,
      remaining: usage.remaining ?? null,
      resetAt: usage.resetAt ?? null
    };

    const state = this._computeState();
    this._emitQuotaAnalytics(state);

    if (state === 'exhausted') {
      this._armResetTimer();
      // If a reply is currently streaming, wait for message:complete so the
      // user sees the response to their 20th message before the input locks.
      // Otherwise (page boot, mock preview, 429 with no stream) emit now.
      if (!this._overlayShown && !this._isInputBusy()) {
        this._emitLockOverlay();
      }
    } else {
      this._clearResetTimer();
      if (this._overlayShown) {
        // Clear the flag BEFORE emitting so PillRow's Upgrade-pill predicate reads
        // the unlocked state during the synchronous `input:enabled` dispatch.
        this._overlayShown = false;
        this.eventBus.emit?.('input:enabled');
      }
    }

    this._render();
    this.eventBus.emit?.('usage:changed', { state, usage: this._usage });
  }

  /**
   * True iff the next send should be blocked client-side. Used by
   * Input.js's `_isLocked()` to keep the textarea disabled post-exhaustion.
   * Applies on both chat and dashboard — the overlay payload differs per
   * page but the textarea is disabled in either case.
   * @returns {boolean}
   */
  isBlocked() {
    this._rolloverIfExpired();
    return this._computeState() === 'exhausted';
  }

  /**
   * Short relative string e.g. "6h 23m". Computed once at emit time — not
   * ticked live.
   * @returns {string}
   */
  getResetCountdownText() {
    if (!this._usage.resetAt) return '';
    const now = Date.now();
    const ms = Math.max(0, new Date(this._usage.resetAt).getTime() - now);
    const totalMinutes = Math.floor(ms / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours > 0) return `Resets in ${hours}h ${minutes}m`;
    if (minutes > 0) return `Resets in ${minutes}m`;
    return 'Resets shortly';
  }

  /**
   * Clear local state on logout so one user's state can't leak into another's
   * session on a shared device.
   */
  reset() {
    this._clearResetTimer();
    this._usage = { unlimited: true, limit: null, used: null, remaining: null, resetAt: null };
    // A different user on this device shares the UTC-day identity, so their
    // transitions must not be suppressed by the previous user's dedup marks.
    this._analyticsFiredFor = {};
    if (this._overlayShown) {
      this._overlayShown = false;
      this.eventBus.emit?.('input:enabled');
    }
    this._render();
  }

  /**
   * Devtools helper. Accepts a partial usage shape and re-renders.
   * @param {Object} overrides
   */
  setMockState(overrides) {
    this.applyServerUsage({ ...this._usage, ...overrides });
  }

  // ─── private ─────────────────────────────────────────────────────────────

  /**
   * UTC-midnight rollover. Once `resetAt` passes, the exhausted snapshot is
   * yesterday's — but a locked tab would otherwise hold it until a manual
   * reload, since isBlocked() suppresses the very sends whose responses
   * refresh state. Optimistically clear the exhausted state (unlock input,
   * drop banner/overlay) and resync the real numbers in the background via
   * the normal initialize() fetch path. Idempotent and race-safe: after the
   * clear the state is no longer exhausted, so re-entry is a no-op.
   * @private
   */
  _rolloverIfExpired() {
    if (this._computeState() !== 'exhausted') return;
    const { resetAt } = this._usage;
    if (!resetAt || new Date(resetAt).getTime() > Date.now()) return;

    this._clearResetTimer();
    this._usage = { ...this._usage, used: 0, remaining: this._usage.limit, resetAt: null };
    if (this._overlayShown) {
      // Same ordering contract as applyServerUsage: flag first, then emit.
      this._overlayShown = false;
      this.eventBus.emit?.('input:enabled');
    }
    this._render();
    this.eventBus.emit?.('usage:changed', { state: this._computeState(), usage: this._usage });
    // Fire-and-forget resync — initialize() catches its own failures.
    this.initialize();
  }

  /**
   * Arm the single reset timer for the current `resetAt`. Re-arming replaces
   * any prior timer, so repeated exhausted snapshots never stack timeouts.
   * The +1s pad lands the fire safely past resetAt; the cap keeps a bogus
   * far-future resetAt from scheduling an absurd delay (an early fire just
   * re-checks in _rolloverIfExpired and no-ops).
   * @private
   */
  _armResetTimer() {
    this._clearResetTimer();
    const { resetAt } = this._usage;
    if (!resetAt) return;
    const delay = new Date(resetAt).getTime() - Date.now();
    if (!Number.isFinite(delay)) return;
    this._resetTimer = setTimeout(
      () => this._rolloverIfExpired(),
      Math.min(Math.max(delay, 0) + 1000, MAX_RESET_TIMER_MS)
    );
  }

  _clearResetTimer() {
    clearTimeout(this._resetTimer);
    this._resetTimer = null;
  }

  _computeState() {
    const { unlimited, limit, used } = this._usage;
    if (unlimited || limit == null || used == null) return 'unlimited';
    if (used >= limit) return 'exhausted';
    if (used >= WARN_RATIO * limit) return 'warning';
    return 'normal';
  }

  _render() {
    const banner = document.getElementById('usage-banner');
    if (!banner) return;

    const state = this._computeState();
    const show = state === 'warning' || state === 'exhausted';
    const text = show ? this._bannerText(state) : '';

    if (show === this._lastBanner.show && text === this._lastBanner.text) return;
    this._lastBanner = { show, text };
    clearTimeout(this._bannerClearTimer);

    if (show) {
      this._writeBanner(banner, text);
      banner.classList.add('usage-banner--visible');
    } else {
      // Remove the class only — content stays through the 300ms height
      // collapse (clearing immediately would animate an empty yellow strip).
      // Clear on a timer so the hidden-but-focusable upgrade button doesn't
      // linger in the a11y tree / tab order; a timer (not transitionend)
      // also fires under reduced-motion.
      banner.classList.remove('usage-banner--visible');
      this._bannerClearTimer = setTimeout(() => { banner.innerHTML = ''; }, 350);
    }
  }

  _bannerText(state) {
    if (state === 'exhausted') return 'Daily limit reached';
    const { limit, used } = this._usage;
    const remaining = Math.max(0, (limit ?? 0) - (used ?? 0));
    return `${remaining} message${remaining === 1 ? '' : 's'} left today`;
  }

  _writeBanner(el, text) {
    el.innerHTML = `
      <div class="usage-banner__content">
        <span class="usage-banner__text">${text}</span>
        <button type="button" class="usage-banner__link">Upgrade for unlimited</button>
      </div>
    `;
    const linkBtn = el.querySelector('.usage-banner__link');
    if (linkBtn) linkBtn.onclick = () => goToUpgrade(this.eventBus, 'usageBanner');
  }

  /**
   * Emit the quota transition events and the usage-banner impression for a
   * freshly applied snapshot. Each event fires once per `resetAt` identity
   * per state (`_analyticsFiredFor`), so repeated snapshots inside a state
   * — every chat response re-applies usage — don't re-fire, while the next
   * UTC day's snapshot does.
   *
   * `surface` is a bounded vocabulary (`chat | dashboard`) — the same page
   * split `_emitLockOverlay` renders against.
   *
   * @param {'unlimited'|'normal'|'warning'|'exhausted'} state
   * @private
   */
  _emitQuotaAnalytics(state) {
    if (state !== 'warning' && state !== 'exhausted') return;

    const surface = this._isOnDashboard() ? 'dashboard' : 'chat';
    this._emitOncePerReset(`quota:${state}`, () => {
      this.eventBus.emit?.(
        state === 'exhausted' ? 'quota:exhausted' : 'quota:warning',
        { surface }
      );
    });
    // The banner renders (with its upgrade CTA) in both states — one
    // impression per state per day. AnalyticsService forwards `upgrade:shown`
    // to GA4 as `upgrade_prompt_shown`.
    this._emitOncePerReset(`banner:${state}`, () => {
      this.eventBus.emit?.('upgrade:shown', { component: 'usageBanner', trigger: state });
    });
  }

  /**
   * Run `emit` once per (key, resetAt) pair. The snapshot's `resetAt` is the
   * UTC-day identity; a null resetAt (mock states, cleared rollover) still
   * dedupes under a sentinel so devtools toggling can't spam events.
   *
   * @param {string} key - Dedup slot, e.g. 'quota:exhausted'
   * @param {Function} emit - Called when this slot hasn't fired for the identity
   * @private
   */
  _emitOncePerReset(key, emit) {
    const identity = this._usage.resetAt ?? 'no-reset-at';
    if (this._analyticsFiredFor[key] === identity) return;
    this._analyticsFiredFor[key] = identity;
    emit();
  }

  _emitLockOverlay() {
    // Set the flag BEFORE emitting: `input:disabled` is dispatched synchronously and
    // PillRow's "Upgrade" pill predicate reads `isLockActive()` during that dispatch,
    // so the flag must already be true or the pill misses this evaluation pass.
    this._overlayShown = true;
    // Overlay impression — once per UTC day, even though page changes re-emit
    // the overlay payload itself.
    this._emitOncePerReset('overlay:exhausted', () => {
      this.eventBus.emit?.('upgrade:shown', { component: 'lockOverlay', trigger: 'exhausted' });
    });
    if (this._isOnDashboard()) {
      // Dashboard: the floating PillRow "Upgrade" CTA never renders here (gated to
      // chat/composer states), so the overlay carries its own inline text-link CTA
      // instead of relying solely on the top-of-page usage banner.
      const countdown = this.getResetCountdownText();
      this.eventBus.emit?.('input:disabled', {
        title: 'Daily limit reached',
        subtitle: countdown ? `${countdown}.` : '',
        variant: 'usage-locked',
        link: { label: 'Upgrade for unlimited', page: 'upgrade' }
      });
    } else {
      // Chat: normal-height title + subtitle overlay with the amber-fill variant.
      // The "Upgrade" call-to-action rides the floating PillRow above the input
      // (registered in app.js, gated on `isLockActive()`), not inside the overlay.
      const countdown = this.getResetCountdownText();
      const upgradeLine = "Upgrade for unlimited messages.";
      this.eventBus.emit?.('input:disabled', {
        title: 'Daily limit reached',
        subtitle: countdown ? `${countdown}. ${upgradeLine}` : upgradeLine,
        variant: 'usage-locked'
      });
    }
  }

  /**
   * Whether the usage-lock overlay is currently active. Drives the floating "Upgrade"
   * pill in the PillRow. PillRow gates pills to chat/composer states, so this need not
   * itself exclude the dashboard (where the top-of-page banner carries the CTA).
   * @returns {boolean}
   */
  isLockActive() {
    return this._overlayShown;
  }

  /**
   * True if the chat input is currently submitting or animating a programmatic
   * message — i.e. a stream is in progress. Used to defer the lock overlay
   * until the current reply finishes.
   */
  _isInputBusy() {
    const input = window.app?.modules?.input;
    return Boolean(input?.isSubmitting || input?.chatBusy);
  }

  _handleMessageComplete() {
    if (this._computeState() === 'exhausted' && !this._overlayShown) {
      this._emitLockOverlay();
    }
  }

  /**
   * Re-emit the lock overlay on page change. Its payload differs between
   * dashboard (minimal, plain white) and chat (full, yellow-bordered card
   * with title/subtitle/upgrade button), so the shape must be swapped on
   * transition. The banner needs no re-render — it is global and static, so
   * a page change cannot change its state.
   */
  _handlePageChanged() {
    const exhausted = this._computeState() === 'exhausted';
    if (exhausted && !this._isInputBusy()) {
      this._emitLockOverlay();
    } else if (this._overlayShown && !exhausted) {
      this._overlayShown = false;
      this.eventBus.emit?.('input:enabled');
    }
  }

  /**
   * Ask PageManager for the authoritative page state rather than scraping
   * `.main--dashboard` off the DOM. PageManager owns `currentMainState` and
   * assigns it before emitting `page:changed`, so our listener is in sync.
   */
  _isOnDashboard() {
    return window.app?.modules?.pageManager?.currentMainState === 'dashboard';
  }

  /**
   * Adopt the refreshed user and refetch usage. `handleGuestConversion` emits
   * `user:updated` with `{ user }` once a guest becomes an account, which is
   * what turns the guest-boot skip in `initialize()` into a real fetch. The
   * response-metadata channel remains the refresh path for tier changes.
   * @param {Object} data - `{ user }` envelope, or a bare user object
   */
  _handleUserUpdated(data) {
    this.currentUser = data?.user || data;
    this.initialize();
  }
}
